import api, { PaginatedApiResponse, ApiResponse } from './axiosInstance';

export interface IProjectDocument {
  documentName: string;
  documentType: 'Manual' | 'Specification' | 'Certificate' | 'Warranty' | 'Installation' | 'Maintenance' | 'Other';
  documentFile: string;
  uploadedAt?: Date;
  uploadedBy?: string;
}

export interface ProjectListParams {
  search?: string;
  projectName?: string;
  projectId?: string;
  status?: string;
  approvalStatus?: string;
  projectStatus?: 'started';
  priority?: string;
  customerId?: string;
  locationId?: string;
  location?: string;
  projectTypeId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  page?: number;
}

export interface CreateProjectRequest {
  projectId: string;
  projectName: string;
  startDate: string; // DD/MM/YYYY or YYYY-MM-DD
  endDate?: string; // DD/MM/YYYY or YYYY-MM-DD
  lastmodifiedDate: string; // DD/MM/YYYY HH:MM AM/PM
  dateCreated: string; // DD/MM/YYYY HH:MM AM/PM
  projectManager: string;
  projectHead?: string; // Optional: Project head user ID
  projectType: string;
  projectCategory: string;
  customerId: string;
  locationId?: string; // Optional: Can be empty if user doesn't select location
  projectTypeId: string;
  manager?: string; // User ObjectId for manager
  // Assigned Resources
  assignedResources?: {
    projectManager?: string; // User ObjectId for project manager
    projectHead?: string; // User ObjectId for project head
    siteEngineers?: string[]; // Array of User ObjectIds for site engineers
    siteSupervisors?: string[]; // Array of User ObjectIds for site supervisors
  };
  // Location and coordinates
  location?: string;
  latitude?: string;
  longitude?: string;
  // Optional legacy/additional fields expected by backend
  name?: string;
  code?: string;
  // Optional documents
  attachedDocuments?: IProjectDocument[];
}

export interface UpdateProjectRequest extends Partial<CreateProjectRequest> {
  capacity?: {
    installedCapacity?: number;
    plannedCapacity?: number;
    unit?: 'MW' | 'KW' | 'Units';
  };
}

// Project Approval Interfaces
export interface SubmitForApprovalRequest {
  comments?: string;
}

export interface ReopenForEditingRequest {
  // No body needed
}

export interface ApproveProjectRequest {
  comments?: string;
}

export interface RejectProjectRequest {
  reason: string;
}

export interface ProjectSiteOptions {
  siteLabels: string[];
  wtgLabels: string[];
}

export const projectService = {
  async getProjects(params?: ProjectListParams) {
    const response = await api.get<PaginatedApiResponse<any>>('/projects', { params });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch projects');
    return response.data;
  },

  async getProjectById(projectId: string) {
    const response = await api.get<ApiResponse<any>>(`/projects/${projectId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch project');
    return response.data.data;
  },

  async createProject(payload: CreateProjectRequest) {
    const response = await api.post<ApiResponse<any>>('/projects', payload);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to create project');
    return response.data.data;
  },

  async updateProject(projectId: string, payload: UpdateProjectRequest) {
    const response = await api.patch<ApiResponse<any>>(`/projects/${projectId}`, payload);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to update project');
    return response.data.data;
  },

  async deleteProject(projectId: string) {
    const response = await api.delete<ApiResponse<null>>(`/projects/${projectId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to delete project');
    return true;
  },

  async getSiteOptions(projectId: string): Promise<ProjectSiteOptions> {
    const response = await api.get<ApiResponse<ProjectSiteOptions>>(`/projects/${projectId}/site-options`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch site options');
    return response.data.data;
  },

  // Project Resource Management
  async addProjectResources(projectId: string, resources: { siteEngineers: string[]; siteSupervisors: string[] }) {
    const response = await api.patch<ApiResponse<any>>(`/projects/${projectId}/resources/add`, resources);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to add resources to project');
    return response.data.data;
  },

  async removeProjectResources(projectId: string, resourceIds: string[]) {
    const response = await api.patch<ApiResponse<any>>(`/projects/${projectId}/resources/remove`, { resourceIds });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to remove resources from project');
    return response.data.data;
  },

  async changeResourceRole(projectId: string, resourceId: string, newRole: 'Site Engineer' | 'Site Supervisor') {
    const response = await api.patch<ApiResponse<any>>(`/projects/${projectId}/resources/${resourceId}/role`, { newRole });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to change resource role');
    return response.data.data;
  },

  // Project Approval Workflow Methods
  async submitProjectForApproval(projectId: string, payload: SubmitForApprovalRequest = {}) {
    const response = await api.post<ApiResponse<any>>(`/projects/${projectId}/submit-for-approval`, payload);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to submit project for approval');
    return response.data;
  },

  async canEditProject(projectId: string): Promise<boolean> {
    const response = await api.get<ApiResponse<{ canEdit: boolean }>>(`/projects/${projectId}/can-edit`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to check edit permission');
    return response.data.data.canEdit;
  },

  async reopenProjectForEditing(projectId: string) {
    const response = await api.post<ApiResponse<any>>(`/projects/${projectId}/reopen-for-editing`, {});
    if (!response.data.success) throw new Error(response.data.message || 'Failed to reopen project');
    return response.data;
  },

  async approveProject(projectId: string, payload: ApproveProjectRequest = {}) {
    const response = await api.post<ApiResponse<any>>(`/projects/${projectId}/approve`, payload);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to approve project');
    return response.data;
  },

  async rejectProject(projectId: string, payload: RejectProjectRequest) {
    const response = await api.post<ApiResponse<any>>(`/projects/${projectId}/reject`, payload);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to reject project');
    return response.data;
  },

  // Task Generation Settings
  async getTaskGenerationSettings(projectId: string) {
    const response = await api.get<ApiResponse<any>>(`/projects/${projectId}/task-generation-settings`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch task settings');
    return response.data;
  },

  async updateTaskGenerationSettings(projectId: string, settings: any) {
    const response = await api.put<ApiResponse<any>>(`/projects/${projectId}/task-generation-settings`, settings);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to update task settings');
    return response.data;
  },

  async resetTaskGenerationSettings(projectId: string) {
    const response = await api.post<ApiResponse<any>>(`/projects/${projectId}/task-generation-settings/reset`, {});
    if (!response.data.success) throw new Error(response.data.message || 'Failed to reset task settings');
    return response.data;
  },

  // Project Progress (resource-centric task analytics)
  async getProgressResources(projectId: string): Promise<ProjectResourceProgressResponse> {
    const response = await api.get<ApiResponse<ProjectResourceProgressResponse>>(
      `/projects/${projectId}/progress/resources`
    );
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch progress');
    return response.data.data;
  },

  async getProgressResourceDetail(
    projectId: string,
    resourceId: string
  ): Promise<ProjectResourceProgressDetail> {
    const response = await api.get<ApiResponse<ProjectResourceProgressDetail>>(
      `/projects/${projectId}/progress/resources/${resourceId}`
    );
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch resource detail');
    return response.data.data;
  }
};

// ===== Project Progress types =====
export type ResourceTaskStatus =
  | 'pass'
  | 'fail'
  | 'inprogress'
  | 'hold'
  | 'due'
  | 'overdue'
  | 'unassigned'
  | 'skip';

export interface ProjectResourceRow {
  resourceId: string;
  name: string;
  email: string;
  roleName?: string;
  avatarColor: string;
  totalAssigned: number;
  completed: number;
  failed: number;
  pending: number;
  inProgress: number;
  overdue: number;
  skipped: number;
  progress: number;
  avgCompletionMinutes: number;
  lastActivityAt: string | null;
  health: 'good' | 'attention' | 'critical' | 'idle';
}

export interface ProjectResourceProgressResponse {
  projectId: string;
  projectName: string;
  totals: {
    totalTasks: number;
    completed: number;
    failed: number;
    pending: number;
    overdue: number;
    inProgress: number;
    skipped: number;
    completionRate: number;
  };
  resources: ProjectResourceRow[];
  lastUpdated: string;
}

export interface ResourceTaskItem {
  taskId: string;
  status: ResourceTaskStatus;
  checkpointName: string;
  frequency?: string;
  outcome?: string;
  startDate: string | null;
  plannedDate: string | null;
  passDateTime: string | null;
  failDateTime: string | null;
  taskStartDate: string | null;
  actualWorkingMinutes: number;
  remarks?: string;
  hasIssue: boolean;
  issueStatus?: string;
  beforeImages: string[];
  afterImages: string[];
  equipmentId?: string;
  equipmentName?: string;
  projectAssetId?: string;
  projectAssetName?: string;
}

export type ResourceTaskGroupNode =
  | {
      level: 'date' | 'asset' | 'equipment';
      key: string;
      label: string;
      taskCount: number;
      children: ResourceTaskGroupNode[];
    }
  | {
      level: 'task';
      key: string;
      label: string;
      taskCount: 1;
      children: [];
      task: ResourceTaskItem;
    };

export interface ProjectResourceProgressDetail {
  resource: {
    resourceId: string;
    name: string;
    email: string;
    roleName?: string;
  };
  totals: {
    totalAssigned: number;
    completed: number;
    failed: number;
    pending: number;
    inProgress: number;
    overdue: number;
    skipped: number;
    completionRate: number;
    avgCompletionMinutes: number;
    medianCompletionMinutes: number;
  };
  groupedByStatus: Array<{
    status: 'completed' | 'pending' | 'inProgress' | 'failed' | 'overdue' | 'skipped';
    label: string;
    count: number;
    tree: ResourceTaskGroupNode[];
  }>;
  kpis: {
    dailyCompletion: Array<{ date: string; completed: number; failed: number }>;
    hourOfDayActivity: Array<{ hour: number; count: number }>;
    workingTimeBuckets: Array<{ bucket: string; count: number }>;
    fastestCompletionMinutes: number;
    slowestCompletionMinutes: number;
  };
  lastUpdated: string;
}

export default projectService;



